import type { ProviderAuthenticationIntentResponse, UniLoginHostedAuthStatusResponse, UniLoginProvider } from '@unipile/types';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useUniLoginRequest } from '../features/accounts/api/create-unilogin-request';
import { type UniLoginRequestStatus, useUniLoginRequestStatus } from '../features/accounts/api/get-unilogin-request-status';
import {
  canUseAutomaticExtensionBridge,
  createUniLoginBridge,
  detectExtensionBrowser,
  readExtensionUpdate,
  type UniLoginBridge,
  type UniLoginExtensionUpdate,
} from '../libs/unilogin-bridge';

export type UniLoginFlowStatus =
  | 'starting'
  | 'extension_missing'
  | 'permission'
  | 'login'
  | 'uploading'
  | 'backend'
  | 'account_mismatch'
  | 'declined'
  | 'expired'
  | 'error';

/** `error` carries the extension error code; `bridge_unavailable` means the extension did not answer at all. */
export type UniLoginExtensionWarning = { type: 'error'; code: string | null } | { type: 'bridge_unavailable' };

export type UniLoginFlowState = {
  status: UniLoginFlowStatus;
  extension_warning: UniLoginExtensionWarning | null;
  /** The last status poll failed temporarily; polling goes on. */
  backend_retrying: boolean;
};

type UniLoginFlowAction =
  | { type: 'request_failed'; inactive: boolean }
  | { type: 'extension_detected'; available: boolean }
  | { type: 'extension_retried' }
  | { type: 'extension_updated'; update: UniLoginExtensionUpdate }
  | { type: 'extension_unreachable' }
  | { type: 'server_updated'; status: UniLoginHostedAuthStatusResponse['status'] }
  | { type: 'server_failed'; reason: StatusErrorReason }
  | { type: 'restarted' };

type StatusErrorReason = 'inactive' | 'rejected' | 'transient';

const EXTENSION_DETECTION_INTERVAL_MS = 3_000;
const EXTENSION_STATUS_POLL_INTERVAL_MS = 1_000;
const SERVER_STATUS_POLL_INTERVAL_MS = 1_500;
const SERVER_STATUS_RETRY_DELAY_MS = 5_000;

const INITIAL_STATE: UniLoginFlowState = { status: 'starting', extension_warning: null, backend_retrying: false };

/** Waiting for the extension to be found. */
const DETECTION_STATUSES: readonly UniLoginFlowStatus[] = ['starting', 'extension_missing'];
/** The extension drives the flow: its own status wins over the server's until the capture is uploaded. */
const EXTENSION_STATUSES: readonly UniLoginFlowStatus[] = ['permission', 'login', 'uploading'];
const TERMINAL_STATUSES: readonly UniLoginFlowStatus[] = ['declined', 'expired', 'error'];

/**
 * Drives the UniLogin connection: creates the UniLogin request, finds the extension through the bridge, follows
 * both the extension and the server status, and hands the account (or checkpoint) to `onSuccess` exactly once.
 * Connection refusals from the Gateway go to `onError`, like with the other login methods.
 */
export function useUniLoginFlow({
  provider,
  onSuccess,
  onError,
}: {
  provider: UniLoginProvider;
  onSuccess: (data: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
}) {
  const [state, dispatch] = useReducer(reduceUniLoginFlow, INITIAL_STATE);
  const browser = useMemo(() => detectExtensionBrowser(typeof navigator === 'undefined' ? '' : navigator.userAgent), []);
  const bridge = useRef<UniLoginBridge | null>(null);
  const completed = useRef(false);
  const mountedAt = useRef(0);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  const requestQuery = useUniLoginRequest({ provider });
  const request = requestQuery.data;
  const statusQuery = useUniLoginRequestStatus({
    provider,
    request_id: request?.request_id,
    queryConfig: {
      enabled: !TERMINAL_STATUSES.includes(state.status),
      retry: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: true,
      refetchInterval: (query) => nextStatusPollDelay(query.state),
    },
  });

  useEffect(() => {
    mountedAt.current = Date.now();
  }, []);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  useEffect(
    () => () => {
      bridge.current?.dispose();
      bridge.current = null;
    },
    [],
  );

  useEffect(() => {
    if (requestQuery.error) dispatch({ type: 'request_failed', inactive: isInactiveRequest(requestQuery.error) });
  }, [requestQuery.error, requestQuery.errorUpdatedAt]);

  useEffect(() => {
    const data = statusQuery.data;
    if (!data) return;
    if (data.object === 'UniLoginHostedAuthStatus') {
      dispatch({ type: 'server_updated', status: data.status });
      return;
    }
    if (completed.current) return;
    completed.current = true;
    onSuccessRef.current(data);
  }, [statusQuery.data, statusQuery.dataUpdatedAt]);

  useEffect(() => {
    const error = statusQuery.error;
    if (!error) return;
    const reason = classifyStatusError(error);
    dispatch({ type: 'server_failed', reason });
    // The Gateway refused the connection (e.g. account already exists): the wizard shows it or redirects. An error
    // cached before this form was mounted (switching tabs) was already reported.
    if (reason === 'rejected' && statusQuery.errorUpdatedAt >= mountedAt.current) onErrorRef.current(error);
  }, [statusQuery.error, statusQuery.errorUpdatedAt]);

  // Looks for the extension, then keeps looking every few seconds so installing it unblocks the page. The bridge
  // is recreated on each attempt because a freshly installed extension only reaches newly loaded bridge pages.
  const detecting = state.status === 'starting' || state.status === 'extension_missing';
  useEffect(() => {
    if (!request || !detecting) return;
    if (!browser || !canUseAutomaticExtensionBridge(request.extension_bridge_url)) {
      dispatch({ type: 'extension_detected', available: false });
      return;
    }
    let cancelled = false;
    let timer: number | undefined;
    const detect = async (): Promise<void> => {
      bridge.current?.dispose();
      const current = createUniLoginBridge(request.extension_bridge_url);
      bridge.current = current;
      const available = await current
        .call('probe')
        .then((payload) => readExtensionUpdate(payload).ok)
        .catch(() => false);
      if (cancelled) return;
      dispatch({ type: 'extension_detected', available });
      if (!available) {
        timer = window.setTimeout(() => void detect(), EXTENSION_DETECTION_INTERVAL_MS);
        return;
      }

      // Starting the connection outlives detection: entering permission stops this effect's timer.
      // Ignore replies from a bridge replaced by a restart, or disposed on unmount.
      try {
        const update = readExtensionUpdate(await current.call('start', request));
        if (bridge.current === current && !completed.current) dispatch({ type: 'extension_updated', update });
      } catch {
        if (bridge.current === current && !completed.current) dispatch({ type: 'extension_unreachable' });
      }
    };
    void detect();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [browser, detecting, request]);

  const extensionActive = EXTENSION_STATUSES.includes(state.status);
  useEffect(() => {
    if (!request || !extensionActive) return;
    let cancelled = false;
    let polling = false;
    const poll = async (): Promise<void> => {
      if (polling || !bridge.current) return;
      polling = true;
      try {
        const update = readExtensionUpdate(await bridge.current.call('status', request));
        if (!cancelled && update.ok) dispatch({ type: 'extension_updated', update });
      } catch {
        // The server status poll stays authoritative during a short bridge interruption.
      } finally {
        polling = false;
      }
    };
    void poll();
    const interval = window.setInterval(() => void poll(), EXTENSION_STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [extensionActive, request]);

  const runExtension = useCallback(async (): Promise<void> => {
    const current = bridge.current;
    if (!current || !request) return;
    dispatch({ type: 'extension_retried' });
    try {
      dispatch({ type: 'extension_updated', update: readExtensionUpdate(await current.call('retry', request)) });
    } catch {
      dispatch({ type: 'extension_unreachable' });
    }
  }, [request]);

  const retry = useCallback(() => void runExtension(), [runExtension]);

  const { refetch: refetchRequest } = requestQuery;
  const restart = useCallback(() => {
    dispatch({ type: 'restarted' });
    void refetchRequest();
  }, [refetchRequest]);

  return { state, request, browser, retry, restart };
}

function reduceUniLoginFlow(state: UniLoginFlowState, action: UniLoginFlowAction): UniLoginFlowState {
  if (action.type === 'restarted') return INITIAL_STATE;
  // Late answers (a slow bridge call, an in-flight poll) must not reopen a finished flow.
  if (TERMINAL_STATUSES.includes(state.status)) return state;

  switch (action.type) {
    case 'request_failed':
      return patch(state, { status: action.inactive ? 'expired' : 'error' });
    case 'extension_detected':
      if (!DETECTION_STATUSES.includes(state.status)) return state;
      return patch(state, { status: action.available ? 'permission' : 'extension_missing', extension_warning: null });
    case 'extension_retried':
      if (!EXTENSION_STATUSES.includes(state.status) && state.status !== 'account_mismatch') return state;
      return patch(state, { status: 'uploading', extension_warning: null });
    case 'extension_updated': {
      if (!EXTENSION_STATUSES.includes(state.status)) return state;
      const { update } = action;
      if (!update.ok) return patch(state, { extension_warning: { type: 'error', code: update.error } });
      return patch(state, {
        status: extensionFlowStatus(update.state) ?? state.status,
        extension_warning: update.error ? { type: 'error', code: update.error } : null,
      });
    }
    case 'extension_unreachable':
      if (!EXTENSION_STATUSES.includes(state.status)) return state;
      return patch(state, { extension_warning: { type: 'bridge_unavailable' } });
    case 'server_updated':
      return applyServerStatus(patch(state, { backend_retrying: false }), action.status);
    case 'server_failed':
      if (action.reason === 'transient') return patch(state, { backend_retrying: true });
      return patch(state, {
        status: action.reason === 'inactive' ? 'expired' : 'error',
        extension_warning: null,
        backend_retrying: false,
      });
  }
}

function applyServerStatus(state: UniLoginFlowState, status: UniLoginHostedAuthStatusResponse['status']): UniLoginFlowState {
  if (status === 'declined' || status === 'expired') return patch(state, { status, extension_warning: null });
  // While the extension works (permission, sign-in, upload), the server only sees an in-progress request: the
  // extension status is the precise one. A mismatch reported before a retry is stale once the retry started.
  if (status === 'account_mismatch') {
    return EXTENSION_STATUSES.includes(state.status)
      ? state
      : patch(state, { status: 'account_mismatch', extension_warning: null });
  }
  const serverProcessing = status === 'approved' || status === 'processing';
  if (DETECTION_STATUSES.includes(state.status) && (status === 'claimed' || serverProcessing)) {
    return patch(state, { status: 'backend', extension_warning: null });
  }
  if (state.status === 'uploading' && serverProcessing) return patch(state, { status: 'backend', extension_warning: null });
  return state;
}

function extensionFlowStatus(state: string | null): UniLoginFlowStatus | null {
  if (state === 'authorization_required') return 'permission';
  if (state === 'login_required') return 'login';
  if (state === 'collecting' || state === 'uploading') return 'uploading';
  if (state === 'uploaded') return 'backend';
  return null;
}

/** Returns the same state when nothing changes, so unchanged polls do not re-render. */
function patch(state: UniLoginFlowState, changes: Partial<UniLoginFlowState>): UniLoginFlowState {
  const next = { ...state, ...changes };
  return next.status === state.status &&
    next.backend_retrying === state.backend_retrying &&
    isSameWarning(next.extension_warning, state.extension_warning)
    ? state
    : next;
}

function isSameWarning(a: UniLoginExtensionWarning | null, b: UniLoginExtensionWarning | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.type === 'error' && b.type === 'error') return a.code === b.code;
  return a.type === b.type;
}

function nextStatusPollDelay(query: {
  data: UniLoginRequestStatus | undefined;
  error: unknown;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
}): number | false {
  const { data, error } = query;
  if (data && data.object !== 'UniLoginHostedAuthStatus') return false;
  if (data?.status === 'declined' || data?.status === 'expired') return false;
  if (error && query.errorUpdatedAt > query.dataUpdatedAt) {
    return classifyStatusError(error) === 'transient' ? SERVER_STATUS_RETRY_DELAY_MS : false;
  }
  return SERVER_STATUS_POLL_INTERVAL_MS;
}

function classifyStatusError(error: unknown): StatusErrorReason {
  if (isInactiveRequest(error)) return 'inactive';
  if (isAxiosError(error) && [400, 401, 403, 409].includes(error.response?.status ?? 0)) return 'rejected';
  return 'transient';
}

/** The Hosted Auth session or the UniLogin request is gone (used, expired or replaced). */
function isInactiveRequest(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const data: unknown = error.response?.data;
  if (typeof data === 'object' && data !== null && 'type' in data && typeof data.type === 'string') {
    if (data.type.startsWith('provider/')) return error.response?.status === 410;
  }
  return [401, 404, 410].includes(error.response?.status ?? 0);
}
